import React, { useState } from 'react';
import { Auth } from 'aws-amplify';

import {
  Button,
  Checkbox,
  message,
  Modal,
  Result,
  Select,
  Space,
  Spin,
} from 'antd';

import axios from 'axios';
import { useRequest } from 'ahooks';
import IframeResizer from 'iframe-resizer-react';

import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { graphConfig, loginScopes } from '../../MSauthConfig';
import { LogoutOutlined, ScissorOutlined } from '@ant-design/icons';
import { AuthenticationResult } from '@azure/msal-browser';
import QrScanner from 'qr-scanner';
import * as pdfjsLib from 'pdfjs-dist';
import { ShortURLResponse } from './AlignmentsTable';
pdfjsLib.GlobalWorkerOptions.workerSrc = '/static/js/pdf.worker.min.js';

const { Option } = Select;

type TemplateCategory = 'SVA' | 'HEDR' | 'NPS' | 'Unknown';
const TEMPLATE_OPTIONS: TemplateCategory[] = ['SVA', 'HEDR', 'NPS', 'Unknown'];

const AlignmentPDFPreview: React.FC<{
  submission_id: string;
  submission_source: string;
  onedrive_file_id?: string | null;
  onQrCodeDetection?: (qrCode: (ShortURLResponse | null)[]) => void;
}> = ({
  submission_id,
  submission_source,
  onedrive_file_id,
  onQrCodeDetection,
}) => {
  const isMSAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const [accessToken, setAccessToken] = useState<null | string>(null);
  const [pagePreviews, setPagePreviews] = useState<string[]>([]);
  const [qrCodesInPages, setQrCodesInPages] = useState<
    (ShortURLResponse | null)[]
  >([]);
  const [selectedPages, setSelectedPages] = useState<boolean[]>([]);
  const [selectedTemplateVersions, setSelectedTemplateVersions] = useState<
    TemplateCategory[]
  >([]);
  const [isSplitModalOpen, setIsSplitModalOpen] = useState(false);
  const [isGeneratingPreviews, setIsGeneratingPreviews] = useState(false);
  const [isSubmittingSplit, setIsSubmittingSplit] = useState(false);

  const getOneDrivePreview = async () => {
    // Silently acquires an access token which is then attached to a request for Microsoft Graph data
    let token = accessToken;
    if (!token) {
      const request = {
        ...loginScopes,
        account: accounts[0],
      };
      let authResult: AuthenticationResult;
      try {
        authResult = await instance.acquireTokenSilent(request);
      } catch (error) {
        authResult = await instance.acquireTokenPopup(request);
      }
      setAccessToken(authResult.accessToken);
      token = authResult.accessToken;
    }

    let drive_id = onedrive_file_id?.split('.')[0];
    let file_id = onedrive_file_id?.split('.')[1];

    // Legacy: if the file id isn't already present, look it up using submission id
    if (!file_id || !drive_id) {
      drive_id = process.env.REACT_APP_ONEDRIVE_DRIVE_ID;
      // Once token acquired, get preview by first getting the file id
      let pathPrefix =
        `/drives/${process.env.REACT_APP_ONEDRIVE_DRIVE_ID}/root:/${process.env.REACT_APP_ONEDRIVE_ROOT_FOLDER}` +
        ([
          'StableMail',
          'EarthClassMail',
          'HelloFax',
          'DropboxUpload',
          'WebUpload',
          'SFTP',
        ].includes(submission_source)
          ? '/OCR' // The automatically OCR'd submission sources get put in a subfolder
          : '');
      let fileType = submission_source === 'LegacyNeueHealth' ? 'tif' : 'pdf';

      const itemByPathResult = await axios.get(
        graphConfig.graphMeEndpoint +
          encodeURIComponent(
            `${pathPrefix}/${submission_source}/${submission_id}.${fileType}`
          ),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      file_id = itemByPathResult.data.id;
    }

    const itemByIdResult = await Promise.all([
      axios.get(
        graphConfig.graphMeEndpoint + `/drives/${drive_id}/items/${file_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      ),
      axios.post(
        graphConfig.graphMeEndpoint +
          `/drives/${drive_id}/items/${file_id}/preview`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      ),
    ]);

    return {
      downloadUrl: itemByIdResult[0].data['@microsoft.graph.downloadUrl'],
      embedUrl: itemByIdResult[1].data.getUrl,
      onedriveFileId: `${drive_id}.${file_id}`,
    };
  };

  const oneDrivePreviewRequest = useRequest(getOneDrivePreview, {
    cacheKey: onedrive_file_id || `${submission_source}:${submission_id}`,
  });
  const effectiveOneDriveFileId =
    oneDrivePreviewRequest.data?.onedriveFileId || onedrive_file_id;

  const getPDFBlob = useRequest(
    () =>
      axios.get(oneDrivePreviewRequest.data!.downloadUrl, {
        responseType: 'arraybuffer',
      }),
    {
      cacheKey: oneDrivePreviewRequest?.data?.downloadUrl,
      ready: !!oneDrivePreviewRequest?.data?.downloadUrl,
      onSuccess: async (result) => {
        const parsedQRCodes = await getQRCodesInPages(result.data);
        setPagePreviews([]);
        setQrCodesInPages(parsedQRCodes);
        setSelectedPages(parsedQRCodes.map(hasMabelQRCode));
        setSelectedTemplateVersions(parsedQRCodes.map(resolveTemplateCategory));
        if (onQrCodeDetection) onQrCodeDetection(parsedQRCodes);
      },
    }
  );

  const isValidHttpsUrl = (s: string) => {
    let url;
    try {
      url = new URL(s);
    } catch (_) {
      return false;
    }
    return url.protocol === 'https:';
  };

  const getQRCodesInPages = async (
    pdfBlob: ArrayBuffer
  ): Promise<(ShortURLResponse | null)[]> => {
    const token = (await Auth.currentSession()).getIdToken().getJwtToken();

    const pdf = await pdfjsLib.getDocument({
      data: pdfBlob,
      isEvalSupported: false,
    }).promise;
    const qrCodesInPages = await Promise.all(
      Array.from({ length: pdf.numPages }, async (_, i) => {
        const page = await pdf.getPage(i + 1);
        const viewport = page.getViewport({ scale: 1 });
        const canvas = new OffscreenCanvas(viewport.width, viewport.height);
        await page.render({
          canvasContext: canvas.getContext('2d')!,
          viewport: viewport,
        }).promise;
        const code = await QrScanner.scanImage(canvas, {
          returnDetailedScanResult: true,
        }).catch((e) => {
          return { data: null };
        });
        if (!code.data || !isValidHttpsUrl(code.data)) {
          return null;
        }
        const response = await axios.get(
          `${process.env.REACT_APP_CHALICE_URL}/parse_short_url?short_url=${code.data}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        return response.data;
      })
    );
    return qrCodesInPages;
  };

  const resolveTemplateCategory = (
    code: ShortURLResponse | null
  ): TemplateCategory => {
    const templateVersion =
      code?.query_params?.template_version ||
      code?.query_params?.dce_form_ycode ||
      '';
    if (!templateVersion) return 'Unknown';
    if (
      templateVersion.startsWith('MPVA') ||
      templateVersion.startsWith('MSVA') ||
      templateVersion.startsWith('SVA')
    ) {
      return 'SVA';
    }
    if (templateVersion.startsWith('HEDR')) {
      return 'HEDR';
    }
    if (templateVersion.startsWith('NPS')) {
      return 'NPS';
    }
    return 'Unknown';
  };

  const hasMabelQRCode = (code: ShortURLResponse | null) =>
    !!code && Object.keys(code.query_params || {}).length > 0;

  const generatePagePreviews = async (pdfBlob: ArrayBuffer) => {
    setIsGeneratingPreviews(true);
    try {
      const pdf = await pdfjsLib.getDocument({
        data: pdfBlob,
        isEvalSupported: false,
      }).promise;
      const previews: string[] = [];
      for (let i = 0; i < pdf.numPages; i++) {
        const page = await pdf.getPage(i + 1);
        const viewport = page.getViewport({ scale: 0.35 });
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          previews.push('');
          continue;
        }
        await page.render({ canvasContext: ctx, viewport }).promise;
        previews.push(canvas.toDataURL('image/png'));
      }
      setPagePreviews(previews);
    } finally {
      setIsGeneratingPreviews(false);
    }
  };

  const openSplitModal = () => {
    if (!getPDFBlob.data?.data) {
      message.error('PDF not ready yet.');
      return;
    }
    setIsSplitModalOpen(true);
    if (!pagePreviews.length) {
      void generatePagePreviews(getPDFBlob.data.data);
    }
  };

  const updatePageSelection = (index: number, checked: boolean) => {
    setSelectedPages((prev) => {
      const next =
        prev.length > 0
          ? [...prev]
          : Array(
              Math.max(pagePreviews.length, qrCodesInPages.length, index + 1)
            ).fill(false);
      next[index] = checked;
      return next;
    });
    if (checked) {
      setSelectedTemplateVersions((prev) => {
        const next =
          prev.length > 0
            ? [...prev]
            : Array(
                Math.max(pagePreviews.length, qrCodesInPages.length, index + 1)
              ).fill('Unknown' as TemplateCategory);
        if (!next[index]) {
          next[index] = resolveTemplateCategory(qrCodesInPages[index]);
        }
        return next;
      });
    }
  };

  const updateTemplateSelection = (index: number, value: TemplateCategory) => {
    setSelectedTemplateVersions((prev) => {
      const next =
        prev.length > 0
          ? [...prev]
          : Array(
              Math.max(pagePreviews.length, qrCodesInPages.length, index + 1)
            ).fill('Unknown' as TemplateCategory);
      next[index] = value;
      return next;
    });
  };

  const submitSplitSelectedPages = async () => {
    if (!effectiveOneDriveFileId) {
      message.error('Missing OneDrive file id.');
      return;
    }
    const selectedPageNumbers = selectedPages
      .map((isSelected, index) => (isSelected ? index + 1 : null))
      .filter((value): value is number => value !== null);
    if (selectedPageNumbers.length === 0) {
      message.warning('Select at least one page to split.');
      return;
    }
    setIsSubmittingSplit(true);
    try {
      const token = (await Auth.currentSession()).getIdToken().getJwtToken();
      await Promise.all(
        selectedPageNumbers.map((selectedPage) => {
          const templateVersion =
            selectedTemplateVersions[selectedPage - 1] || 'Unknown';
          return axios.post(
            `${process.env.REACT_APP_CHALICE_URL}/split_and_save_onedrive_selected_pages`,
            {
              onedrive_file_id: effectiveOneDriveFileId,
              submission_source,
              selected_page: selectedPage,
              template_version: templateVersion,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        })
      );
      message.success(
        `${selectedPageNumbers.length} page${
          selectedPageNumbers.length > 1 ? 's' : ''
        } created`
      );
      setIsSplitModalOpen(false);
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || error?.message || 'Split failed.'
      );
    } finally {
      setIsSubmittingSplit(false);
    }
  };

  return isMSAuthenticated ? (
    <Spin spinning={oneDrivePreviewRequest.loading}>
      <Button
        icon={<LogoutOutlined />}
        type="primary"
        size="small"
        style={{ backgroundColor: '#0001FF', float: 'right', marginBottom: 4 }}
        onClick={() => {
          instance.logoutPopup().catch((e) => {
            console.error(e);
          });
        }}
      >
        Logout of OneDrive{' '}
        <img
          alt="MS Logo"
          src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg"
          style={{ marginLeft: 8, width: 12 }}
        />
      </Button>
      {oneDrivePreviewRequest.error ? (
        <Result
          status="error"
          title="Error retrieving file from OneDrive"
          subTitle={oneDrivePreviewRequest.error.message}
        />
      ) : (
        <Space direction="vertical" style={{ width: '100%' }}>
          <IframeResizer
            src={oneDrivePreviewRequest.data?.embedUrl}
            style={{ width: '1px', minWidth: '100%' }}
            height={800}
            frameBorder={0}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button
              type="primary"
              disabled={!getPDFBlob.data || !effectiveOneDriveFileId}
              onClick={openSplitModal}
              icon={<ScissorOutlined />}
            >
              Split or Move Pages
            </Button>
          </div>
          <Modal
            title="Split or Move Pages"
            visible={isSplitModalOpen}
            onCancel={() => setIsSplitModalOpen(false)}
            width={960}
            footer={[
              <Button
                key="cancel"
                onClick={() => setIsSplitModalOpen(false)}
                disabled={isSubmittingSplit}
              >
                Cancel
              </Button>,
              <Button
                key="split"
                type="primary"
                loading={isSubmittingSplit}
                disabled={!selectedPages.some(Boolean)}
                onClick={submitSplitSelectedPages}
                icon={<ScissorOutlined />}
              >
                Split or Move Selected Pages
              </Button>,
            ]}
          >
            <Spin spinning={isGeneratingPreviews}>
              {pagePreviews.length ? (
                <>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      gap: 8,
                      marginBottom: 12,
                    }}
                  >
                    <Button
                      size="small"
                      onClick={() => {
                        setSelectedPages(pagePreviews.map(() => true));
                        setSelectedTemplateVersions((prev) =>
                          prev.length > 0
                            ? prev
                            : pagePreviews.map((_, index) =>
                                resolveTemplateCategory(qrCodesInPages[index])
                              )
                        );
                      }}
                    >
                      Select All
                    </Button>
                    <Button
                      size="small"
                      onClick={() =>
                        setSelectedPages(pagePreviews.map(() => false))
                      }
                    >
                      Unselect All
                    </Button>
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns:
                        'repeat(auto-fill, minmax(160px, 1fr))',
                      gap: 12,
                    }}
                  >
                    {pagePreviews.map((preview, index) => (
                      <div
                        key={`page-preview-${index + 1}`}
                        style={{
                          border: '1px solid #f0f0f0',
                          borderRadius: 6,
                          padding: 8,
                        }}
                      >
                        <Checkbox
                          checked={!!selectedPages[index]}
                          onChange={(event) =>
                            updatePageSelection(index, event.target.checked)
                          }
                        >
                          Page {index + 1}
                        </Checkbox>
                        <div style={{ marginTop: 8 }}>
                          <img
                            alt={`Page ${index + 1}`}
                            src={preview}
                            style={{
                              width: '100%',
                              height: 'auto',
                              display: 'block',
                            }}
                          />
                        </div>
                        {selectedPages[index] ? (
                          <div style={{ marginTop: 8 }}>
                            <Select
                              size="small"
                              value={
                                selectedTemplateVersions[index] ||
                                resolveTemplateCategory(qrCodesInPages[index])
                              }
                              onChange={(value) =>
                                updateTemplateSelection(
                                  index,
                                  value as TemplateCategory
                                )
                              }
                              style={{ width: '100%' }}
                            >
                              {TEMPLATE_OPTIONS.map((option) => (
                                <Option key={option} value={option}>
                                  {option}
                                </Option>
                              ))}
                            </Select>
                          </div>
                        ) : hasMabelQRCode(qrCodesInPages[index]) ? (
                          <div
                            style={{
                              marginTop: 6,
                              fontSize: 12,
                              color: '#389e0d',
                            }}
                          >
                            QR detected
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div style={{ padding: '12px 0', textAlign: 'center' }}>
                  Generating previews...
                </div>
              )}
            </Spin>
          </Modal>
        </Space>
      )}
    </Spin>
  ) : (
    <Result
      icon={
        <img
          alt="MS Logo"
          src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg"
          width={96}
        />
      }
      subTitle="Login in to OneDrive to view previews"
      extra={
        <Button
          style={{ backgroundColor: '#0001FF' }}
          type="primary"
          onClick={() =>
            instance.loginPopup(loginScopes).catch((e) => {
              message.error(e.message);
            })
          }
        >
          Sign In To OneDrive
        </Button>
      }
    />
  );
};
export default AlignmentPDFPreview;
